'use client';

import React, { useState } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Stack, useMediaQuery } from '@mui/material';
import { color, spacing, borderRadius } from '@/theme/tokens';
import { useTranslations } from 'next-intl';
import IconChevronDown from '@/assets/icons/IconChevronDown';
import SectionWrapper from '@/components/common/SectionWrapper';
import { renderTextWithLinks } from '@/helpers/renderTextWithLinks';
import { HOME_FAQ_IDS } from './faqIds';

export default function FAQ() {
    const t = useTranslations('HomePage.faq');
    const [expanded, setExpanded] = useState<string[]>([]);

    const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(prev => (isExpanded ? [...prev, panel] : prev.filter(id => id !== panel)));
    };

    return (
        <SectionWrapper backgroundColor={color.gray[10]} contentAlignItems="center" contentJustifyContent="center">
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: spacing[48], sm: spacing[64], md: spacing[80] },
                    alignItems: 'center',
                    width: '100%',
                    maxWidth: { sm: '100%', md: '1312px' },
                }}
            >
                <Typography
                    variant="heading5bold"
                    color={color.gray[80]}
                    textAlign="center"
                    component="h2"
                    sx={{
                        maxWidth: { xs: '240px', sm: '704px', md: '1312px' },
                    }}
                >
                    {t('title')}
                </Typography>

                <Box
                    sx={{
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '100%', md: '1088px' },
                    }}
                >
                    {HOME_FAQ_IDS.map(id => (
                        <Accordion
                            key={id}
                            expanded={expanded.includes(id)}
                            onChange={handleChange(id)}
                            sx={{
                                borderRadius: `${spacing[8]} !important`,
                                boxShadow: 'none',
                                marginBottom: spacing[8],
                                marginTop: spacing[0],
                                '&.Mui-expanded': {
                                    marginTop: spacing[8],
                                    marginBottom: spacing[8],
                                },
                                '&:before': {
                                    display: 'none',
                                },
                                '&:last-child': {
                                    marginBottom: 0,
                                },
                                '&:has(.MuiAccordionSummary-root.Mui-focusVisible)': {
                                    outline: `2px solid ${color.gray[80]}`,
                                    outlineOffset: '2px',
                                },
                                '& .MuiAccordionSummary-root': {
                                    '&.Mui-focusVisible, &:focus, &:focus-visible': {
                                        outline: 'none',
                                    },
                                },
                            }}
                        >
                            <AccordionSummary
                                sx={{
                                    padding: `${spacing[16]} ${spacing[12]} ${spacing[16]} ${spacing[16]}`,
                                    '&.Mui-expanded': {
                                        padding: `${spacing[16]} ${spacing[12]} ${spacing[16]} ${spacing[16]}`,
                                        minHeight: '0',
                                    },
                                    '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
                                        marginTop: 0,
                                        marginBottom: 0,
                                    },
                                    '& .MuiAccordionSummary-content': {
                                        paddingRight: spacing[16],
                                    },
                                }}
                                expandIcon={<IconChevronDown color={color.gray[70]} width={24} height={24} />}
                            >
                                <Typography variant="label6bold" component="span" color={color.gray[80]} sx={{ flex: 1 }}>
                                    {t(`items.${id}.question`)}
                                </Typography>
                            </AccordionSummary>

                            <AccordionDetails
                                sx={{
                                    padding: spacing[16],
                                    paddingTop: 0,
                                }}
                            >
                                <Typography variant="paragraph4regular" component="p" color={color.gray[80]}>
                                    {renderTextWithLinks(t(`items.${id}.answer`))}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Box>
        </SectionWrapper>
    );
}
